import { ReactNode, useCallback } from "react";
import clsx from "clsx";
import { Link, useLocation } from "react-router-dom";

// Components
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";

// MUI
import { Box, MenuItem, MenuList, Typography } from "@mui/material";

// Types
import { LinkTemplate } from "@Common/interfaces/types";

interface ILinkOrAProps {
	link: string;
	externalLink?: boolean;
	children: ReactNode;
	className?: string;
}

const LinkOrA = (props: Readonly<ILinkOrAProps>) => {
	const { link, externalLink, children, className } = props;

	if (externalLink) {
		return (
			<a className={className} href={link} target="_blank" rel="noreferrer">
				{children}
			</a>
		);
	}

	return (
		<Link className={className} to={link}>
			{children}
		</Link>
	);
};

export type TRenderOptionConfiguration = {
	requiredActions: "*" | string[];
	title: string;
	icon: ReactNode;
	link: LinkTemplate | "";
	externalLink?: boolean;
	onClick?: () => void;
};

interface IRenderOptionsProps {
	arrayOptions: TRenderOptionConfiguration[];
}

export const RenderMenuOptions = ({ arrayOptions }: Readonly<IRenderOptionsProps>) => {
	const { isPresentMultipleAccess } = useAuthorization();
	const { pathname } = useLocation();

	const isSelected = useCallback((route: string) => route === pathname, [pathname]);

	return (
		<>
			{arrayOptions.map((element) => {
				const uniqueKey = `${element?.title}`;
				const selected = isSelected(element.link);

				return (
					<MenuItem
						key={uniqueKey}
						selected={selected}
						disabled={!isPresentMultipleAccess(element.requiredActions, "atLeastOne")}
						disableRipple
						className={clsx("group h-8 !py-0 !px-0 hover:bg-table-hover", {
							"!bg-gray-highlight": selected,
						})}
						onClick={element.onClick}
					>
						<LinkOrA
							link={element.link}
							externalLink={element.externalLink}
							className="flex flex-row justify-between w-full !px-5"
						>
							<Typography
								className={clsx({
									"!font-sans pr-[15px] flex-1": true,
									"text-default": !selected,
									"text-white": selected,
								})}
							>
								{element.title}
							</Typography>
							<Box
								className={clsx({
									"text-neutral-gray h-6 w-6 flex": true,
									"group-hover:text-default": !selected,
									"text-white ": selected,
								})}
								justifyContent="center"
								alignItems="center"
							>
								{element.icon}
							</Box>
						</LinkOrA>
					</MenuItem>
				);
			})}
		</>
	);
};

interface IRenderMenusOptionsProps {
	arrayOptions: TRenderOptionConfiguration[];
}

export default function RenderMenusOptions(props: Readonly<IRenderMenusOptionsProps>) {
	return (
		<MenuList id="settings-btn-menu" aria-labelledby="settings-box-button" className={`!rounded-b-[10px] !pt-2 !pb-3`}>
			<RenderMenuOptions arrayOptions={props.arrayOptions} />
		</MenuList>
	);
}
